import { Suspense } from 'react';
import { Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LogOut } from 'lucide-react';
import { useAgency, type SuspendedAgency } from '@/auth/AgencyProvider';
import { useAuth } from '@/auth/AuthProvider';
import { LocaleToggle } from '@/components/LocaleToggle';
import { Button } from '@/components/ui/button';
import { NoAgencyScreen, AgencyErrorBanner } from '@/components/common/NoAgencyScreen';
import { SuspendedAgencyScreen } from '@/components/common/SuspendedAgencyScreen';
import { ProfileBanner } from '@/components/profile/ProfileBanner';
import { RouteFallback } from '@/components/common/RouteFallback';
import { Toaster } from '@/components/ui/sonner';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import { Wordmark } from './Wordmark';

/**
 * Fixed rail on the left, top bar, white cards on the grey canvas, dark brand footer.
 *
 * The rail is `sticky`, not `fixed`, and the page is a flex COLUMN whose last child is the footer.
 * That is what lets the navy footer run the whole width, under the rail as well, the way the mockup
 * ends the screen: a fixed rail is glued to the viewport and would paint straight over it.
 */
export function AppLayout() {
  const { t } = useTranslation();
  const agency = useAgency();

  // Every membership in a suspended agency: nothing of the portal would load against zero agencies, so
  // no rail, no menu, no top bar. Only the paused screen, the language switch and the sign-out.
  if (agency.status === 'suspended') return <SuspendedLayout agencies={agency.agencies} />;

  return (
    <div className="flex min-h-screen flex-col bg-canvas">
      <div className="flex flex-1">
        {/* The navy belongs to the COLUMN, not to the sticky rail inside it: once the page is taller
            than the viewport the rail is only one screen tall, and without this the canvas would show
            through underneath it. */}
        <div className="w-rail shrink-0 bg-rail">
          <Sidebar />
        </div>
        <div className="flex min-w-0 flex-1 flex-col">
          <Topbar />
          <main className="flex-1 p-4 sm:p-6">
            {/* A signed-in account with no office role (a driver) sees why, with the sign-out still in
                the rail. A read error does NOT block the portal: the banner offers a retry and gated
                actions stay hidden (no role = nothing allowed). */}
            {agency.status === 'none' ? (
              <NoAgencyScreen />
            ) : (
              <>
                {agency.status === 'error' && <AgencyErrorBanner onRetry={agency.reload} />}
                <ProfileBanner />
                {/* Screens are lazy chunks (App.tsx): the layout stays while one downloads. */}
                <Suspense fallback={<RouteFallback />}>
                  <Outlet />
                </Suspense>
              </>
            )}
          </main>
        </div>
      </div>

      <footer className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 bg-rail px-6 py-3">
        <div className="flex items-center gap-2">
          <Wordmark tone="rail" size="sm" layout="inline" />
          <span className="rounded-full bg-rail-2 px-2 py-0.5 text-xs font-medium text-on-rail-muted">
            {t('brand.version')}
          </span>
        </div>
        <p className="text-sm text-on-rail-muted">{t('footer.tagline')}</p>
        <p className="text-sm text-on-rail-muted">{t('footer.attribution')}</p>
      </footer>
      <Toaster />
    </div>
  );
}

/** The whole page while access is paused: the brand bar with the language switch and the sign-out,
 *  then the paused card. No navigation at all (change spec operator-agency-onboarding, design 2). */
function SuspendedLayout({ agencies }: { agencies: SuspendedAgency[] }) {
  const { t } = useTranslation();
  const { signOut } = useAuth();

  return (
    <div className="flex min-h-screen flex-col bg-canvas">
      <header className="flex flex-wrap items-center gap-x-6 gap-y-2 bg-rail px-4 py-3 sm:px-6">
        <Wordmark tone="rail" size="sm" layout="inline" />
        <div className="ml-auto flex items-center gap-3">
          <div className="rounded-md bg-card">
            <LocaleToggle />
          </div>
          <Button type="button" variant="outline" size="sm" onClick={() => void signOut()}>
            <LogOut className="size-4" aria-hidden="true" />
            {t('user.signOut')}
          </Button>
        </div>
      </header>
      <main className="flex-1 p-4 sm:p-6">
        <SuspendedAgencyScreen agencies={agencies} />
      </main>
      <Toaster />
    </div>
  );
}
